/**
 * Keys API - Backend Key Management Endpoints
 *
 * Handles communication with the backend for:
 * - Key registration
 * - Key verification
 * - Challenge-response authentication
 * - Fetching recipient public keys
 */

import {
  AuthChallenge,
  EncryptionRecipient,
  KeyRegistrationRequest,
  KeyRegistrationResponse,
  KeyVerificationResponse,
  LoginResponse,
  PublicKeyInfo,
  SignedChallenge,
} from '@antragsmanager/crypto-lib';

const API_BASE = '/api/v1';

/**
 * Helper to make API requests
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`API Error ${response.status}: ${errorBody}`);
  }

  return response.json();
}

// ============ Authentication ============

/**
 * Register a new citizen (self-registration)
 */
export async function registerCitizen(
  request: KeyRegistrationRequest
): Promise<KeyRegistrationResponse> {
  return apiRequest<KeyRegistrationResponse>('/auth/register/citizen', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Register a new authority (self-registration for Behörden)
 */
export async function registerAuthority(
  request: KeyRegistrationRequest
): Promise<KeyRegistrationResponse> {
  return apiRequest<KeyRegistrationResponse>('/auth/register/authority', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Authority profile data for extended registration
 */
export interface AuthorityProfileRequest {
  organizationName?: string;
  department?: string;
  contactEmail?: string;
  streetAddress?: string;
  postalCode?: string;
  city?: string;
  phone?: string;
  website?: string;
}

/**
 * Extended authority registration request
 */
export interface AuthorityRegistrationRequest {
  ownerName: string;
  publicKeyJwk: JsonWebKey;
  fingerprint: string;
  profile?: AuthorityProfileRequest;
}

/**
 * Extended authority registration response
 */
export interface AuthorityRegistrationResponse {
  keyId: string;
  ownerName: string;
  createdAt: string;
  profile?: AuthorityProfileRequest;
}

/**
 * Register a new authority with optional profile data (extended registration)
 */
export async function registerAuthorityExtended(
  request: AuthorityRegistrationRequest
): Promise<AuthorityRegistrationResponse> {
  return apiRequest<AuthorityRegistrationResponse>('/auth/register/authority/extended', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Verify a key and get role information
 */
export async function verifyKey(keyId: string): Promise<KeyVerificationResponse> {
  return apiRequest<KeyVerificationResponse>(`/auth/verify/${keyId}`);
}

/**
 * Request an authentication challenge
 */
export async function requestChallenge(keyId: string): Promise<AuthChallenge> {
  return apiRequest<AuthChallenge>('/auth/challenge', {
    method: 'POST',
    body: JSON.stringify({ keyId }),
  });
}

/**
 * Submit a signed challenge for login
 */
export async function login(signedChallenge: SignedChallenge): Promise<LoginResponse> {
  return apiRequest<LoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(signedChallenge),
  });
}

// ============ Key Management ============

/**
 * Create an authority key (Admin only)
 */
export async function createAuthorityKey(
  request: KeyRegistrationRequest,
  authToken?: string
): Promise<KeyRegistrationResponse> {
  const headers: Record<string, string> = {};
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  return apiRequest<KeyRegistrationResponse>('/keys/authority', {
    method: 'POST',
    body: JSON.stringify(request),
    headers,
  });
}

/**
 * Create an admin key (Admin only)
 */
export async function createAdminKey(
  request: KeyRegistrationRequest,
  authToken?: string
): Promise<KeyRegistrationResponse> {
  const headers: Record<string, string> = {};
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  return apiRequest<KeyRegistrationResponse>('/keys/admin', {
    method: 'POST',
    body: JSON.stringify(request),
    headers,
  });
}

/**
 * Get a public key by ID
 */
export async function getPublicKey(keyId: string): Promise<PublicKeyInfo> {
  return apiRequest<PublicKeyInfo>(`/keys/public/${keyId}`);
}

/**
 * Get recipient keys for a process (for encryption)
 */
export async function getProcessRecipientKeys(processId: string): Promise<EncryptionRecipient[]> {
  const keys = await apiRequest<PublicKeyInfo[]>(`/keys/process/${processId}`);
  return keys.map((key) => ({
    keyId: key.keyId,
    publicKeyJwk: key.publicKeyJwk,
  }));
}

/**
 * Get all authority keys (for encrypting to authorities)
 */
export async function getAuthorityKeys(): Promise<EncryptionRecipient[]> {
  const keys = await apiRequest<PublicKeyInfo[]>('/keys/authorities');
  return keys.map((key) => ({
    keyId: key.keyId,
    publicKeyJwk: key.publicKeyJwk,
  }));
}

/**
 * Revoke a key (Admin only)
 */
export async function revokeKey(
  keyId: string,
  reason: string,
  authToken?: string
): Promise<void> {
  const headers: Record<string, string> = {};
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  await apiRequest<void>(`/keys/${keyId}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    headers,
  });
}

// ============ Helper Functions ============

/**
 * Get recipient keys for form submission
 *
 * Returns all keys that should receive the encrypted form data:
 * - Authority keys for the process
 * - Optional: Admin keys for oversight
 */
export async function getFormRecipientKeys(_processDefinitionKey: string): Promise<EncryptionRecipient[]> {
  // For now, return all authority keys
  // In a real implementation, this would be more targeted based on the process
  return getAuthorityKeys();
}

/**
 * Check if a key is valid and not revoked
 */
export async function isKeyValid(keyId: string): Promise<boolean> {
  try {
    const response = await verifyKey(keyId);
    return response.valid;
  } catch {
    return false;
  }
}

// ============ Multi-Employee Authority Key Management (GKDK System) ============

/**
 * Authority Key DTO - public information about an authority
 */
export interface AuthorityKeyDto {
  authorityId: string;
  authorityName: string;
  publicKeyJwk: JsonWebKey;
  fingerprint: string;
  currentEpoch: number;
  createdAt: string;
  lastRotationAt: string | null;
  isActive: boolean;
  activeEmployeeCount: number;
}

/**
 * Authority public key for citizens to encrypt
 */
export interface AuthorityPublicKeyDto {
  authorityId: string;
  authorityName: string;
  publicKeyJwk: JsonWebKey;
  fingerprint: string;
  currentEpoch: number;
}

/**
 * Authority Employee DTO
 */
export interface AuthorityEmployeeDto {
  keyId: string;
  name: string;
  fingerprint: string;
  publicKeyJwk: JsonWebKey;
  addedAt: string;
  onboardedBy: string | null;
  lastKnownEpoch: number;
  isActive: boolean;
  revokedAt: string | null;
  revocationReason: string | null;
}

/**
 * Request to create a new authority
 */
export interface CreateAuthorityRequest {
  authorityId: string;
  authorityName: string;
  publicKeyJwk: JsonWebKey;
  fingerprint: string;
  creatorKeyId: string;
  creatorName: string;
  creatorPublicKeyJwk: JsonWebKey;
  creatorFingerprint: string;
}

/**
 * Response after creating authority
 */
export interface CreateAuthorityResponse {
  authority: AuthorityKeyDto;
  creator: AuthorityEmployeeDto;
}

/**
 * Request to onboard a new employee
 */
export interface OnboardEmployeeRequest {
  employeeKeyId: string;
  publicKeyJwk: JsonWebKey;
  fingerprint: string;
  name: string;
  onboardedByKeyId: string;
}

/**
 * Response after onboarding
 */
export interface OnboardEmployeeResponse {
  employee: AuthorityEmployeeDto;
  currentEpoch: number;
}

/**
 * Request to revoke an employee
 */
export interface RevokeEmployeeRequest {
  reason: string;
  revokedByKeyId: string;
}

/**
 * Response after revoking employee
 */
export interface RevokeEmployeeResponse {
  revokedEmployee: AuthorityEmployeeDto;
  newEpoch: number;
  message: string;
}

/**
 * Response after key rotation
 */
export interface RotateKeyResponse {
  authorityId: string;
  newEpoch: number;
  rotatedAt: string;
}

/**
 * Authority with employees
 */
export interface AuthorityWithEmployeesDto {
  authority: AuthorityKeyDto;
  employees: AuthorityEmployeeDto[];
}

/**
 * Audit log entry
 */
export interface AuthorityAuditLogDto {
  operation: string;
  performedByKeyId: string;
  targetKeyId: string | null;
  epoch: number | null;
  timestamp: string;
  details: Record<string, unknown> | null;
}

// ============ Authority Key API Functions ============

/**
 * Create a new authority with its AEK (first employee setup)
 */
export async function createAuthorityWithKey(
  request: CreateAuthorityRequest
): Promise<CreateAuthorityResponse> {
  return apiRequest<CreateAuthorityResponse>('/authority-keys', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Get all active authorities
 */
export async function getAllAuthorityKeys(): Promise<AuthorityKeyDto[]> {
  return apiRequest<AuthorityKeyDto[]>('/authority-keys');
}

/**
 * Get authority by ID
 */
export async function getAuthorityKey(authorityId: string): Promise<AuthorityKeyDto> {
  return apiRequest<AuthorityKeyDto>(`/authority-keys/${authorityId}`);
}

/**
 * Get authority public key for encryption (citizen-facing)
 */
export async function getAuthorityPublicKeyForEncryption(
  authorityId: string
): Promise<AuthorityPublicKeyDto> {
  return apiRequest<AuthorityPublicKeyDto>(`/authority-keys/${authorityId}/public-key`);
}

/**
 * Get authority with all employees
 */
export async function getAuthorityWithEmployees(
  authorityId: string
): Promise<AuthorityWithEmployeesDto> {
  return apiRequest<AuthorityWithEmployeesDto>(`/authority-keys/${authorityId}/details`);
}

/**
 * Search authorities by name
 */
export async function searchAuthorityKeys(name: string): Promise<AuthorityKeyDto[]> {
  return apiRequest<AuthorityKeyDto[]>(`/authority-keys/search?name=${encodeURIComponent(name)}`);
}

/**
 * Deactivate an authority
 */
export async function deactivateAuthority(
  authorityId: string,
  reason: string,
  keyId: string
): Promise<void> {
  await apiRequest<void>(
    `/authority-keys/${authorityId}?reason=${encodeURIComponent(reason)}`,
    {
      method: 'DELETE',
      headers: {
        'X-Key-Id': keyId,
      },
    }
  );
}

// ============ Employee API Functions ============

/**
 * Get all active employees of an authority
 */
export async function getAuthorityEmployees(
  authorityId: string
): Promise<AuthorityEmployeeDto[]> {
  return apiRequest<AuthorityEmployeeDto[]>(`/authority-keys/${authorityId}/employees`);
}

/**
 * Onboard a new employee to an authority
 */
export async function onboardEmployee(
  authorityId: string,
  request: OnboardEmployeeRequest
): Promise<OnboardEmployeeResponse> {
  return apiRequest<OnboardEmployeeResponse>(`/authority-keys/${authorityId}/employees`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Get a specific employee
 */
export async function getEmployee(
  authorityId: string,
  employeeKeyId: string
): Promise<AuthorityEmployeeDto> {
  return apiRequest<AuthorityEmployeeDto>(
    `/authority-keys/${authorityId}/employees/${employeeKeyId}`
  );
}

/**
 * Revoke an employee (triggers key rotation)
 */
export async function revokeEmployee(
  authorityId: string,
  employeeKeyId: string,
  request: RevokeEmployeeRequest
): Promise<RevokeEmployeeResponse> {
  return apiRequest<RevokeEmployeeResponse>(
    `/authority-keys/${authorityId}/employees/${employeeKeyId}`,
    {
      method: 'DELETE',
      body: JSON.stringify(request),
    }
  );
}

/**
 * Update an employee's epoch
 */
export async function updateEmployeeEpoch(
  authorityId: string,
  employeeKeyId: string,
  epoch: number
): Promise<AuthorityEmployeeDto> {
  return apiRequest<AuthorityEmployeeDto>(
    `/authority-keys/${authorityId}/employees/${employeeKeyId}/epoch`,
    {
      method: 'PUT',
      body: JSON.stringify({ epoch }),
    }
  );
}

/**
 * Get employees who need to update to the current epoch
 */
export async function getEmployeesNeedingUpdate(
  authorityId: string
): Promise<AuthorityEmployeeDto[]> {
  return apiRequest<AuthorityEmployeeDto[]>(
    `/authority-keys/${authorityId}/employees/needs-update`
  );
}

/**
 * Check if an employee belongs to an authority
 */
export async function checkEmployeeMembership(
  authorityId: string,
  employeeKeyId: string
): Promise<boolean> {
  const result = await apiRequest<{ isMember: boolean }>(
    `/authority-keys/${authorityId}/employees/${employeeKeyId}/check`
  );
  return result.isMember;
}

// ============ Key Rotation API Functions ============

/**
 * Manually trigger key rotation
 */
export async function rotateAuthorityKey(
  authorityId: string,
  triggeredByKeyId: string
): Promise<RotateKeyResponse> {
  return apiRequest<RotateKeyResponse>(`/authority-keys/${authorityId}/rotate`, {
    method: 'POST',
    body: JSON.stringify({ triggeredByKeyId }),
  });
}

// ============ Audit API Functions ============

/**
 * Get audit log for an authority
 */
export async function getAuthorityAuditLog(
  authorityId: string,
  limit = 50
): Promise<AuthorityAuditLogDto[]> {
  return apiRequest<AuthorityAuditLogDto[]>(
    `/authority-keys/${authorityId}/audit?limit=${limit}`
  );
}

// ============ Employee's Authorities ============

/**
 * Get all authorities an employee belongs to
 */
export async function getAuthoritiesForEmployee(
  employeeKeyId: string
): Promise<AuthorityKeyDto[]> {
  return apiRequest<AuthorityKeyDto[]>(`/authority-keys/by-employee/${employeeKeyId}`);
}

/**
 * Alias for getAuthoritiesForEmployee - get authorities the current employee belongs to
 */
export async function getMyAuthorities(employeeKeyId: string): Promise<AuthorityKeyDto[]> {
  return getAuthoritiesForEmployee(employeeKeyId);
}

// ============ Submissions API Functions ============

/**
 * Submission DTO
 */
export interface SubmissionDto {
  id: string;
  confirmationNumber: string;
  authorityId: string;
  processKey: string;
  submittedAt: string;
  encryptedData: unknown;
  epoch: number;
}

/**
 * Get all submissions for an authority
 */
export async function getSubmissionsForAuthority(
  authorityId: string
): Promise<SubmissionDto[]> {
  return apiRequest<SubmissionDto[]>(`/authority-keys/${authorityId}/submissions`);
}

// ============ GKDK Distribution API Functions ============

/**
 * GKDK Distribution DTO - wrapped bundle for an employee
 */
export interface GKDKDistributionDto {
  employeeKeyId: string;
  wrappedBundle: Record<string, unknown>;
}

/**
 * Request to store GKDK distributions
 */
export interface StoreGKDKDistributionsRequest {
  epoch: number;
  distributions: GKDKDistributionDto[];
  createdByKeyId: string;
}

/**
 * Response when fetching a GKDK distribution
 */
export interface GKDKDistributionResponse {
  authorityId: string;
  employeeKeyId: string;
  epoch: number;
  wrappedBundle: Record<string, unknown>;
  createdByKeyId: string;
}

/**
 * Store GKDK distributions for employees after key rotation
 */
export async function storeGKDKDistributions(
  authorityId: string,
  request: StoreGKDKDistributionsRequest
): Promise<void> {
  const response = await fetch(`${API_BASE}/authority-keys/${authorityId}/gkdk-distributions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`API Error ${response.status}: ${errorBody}`);
  }
  // No need to parse response - endpoint returns void
}

/**
 * Get the latest unconsumed GKDK distribution for an employee
 */
export async function getGKDKForEmployee(
  authorityId: string,
  employeeKeyId: string
): Promise<GKDKDistributionResponse | null> {
  try {
    return await apiRequest<GKDKDistributionResponse>(
      `/authority-keys/${authorityId}/gkdk-distributions/${employeeKeyId}`
    );
  } catch (error) {
    // Return null if not found
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
}

/**
 * Mark a GKDK distribution as consumed
 */
export async function consumeGKDKDistribution(
  authorityId: string,
  employeeKeyId: string,
  epoch: number
): Promise<void> {
  const response = await fetch(
    `${API_BASE}/authority-keys/${authorityId}/gkdk-distributions/${employeeKeyId}/consume`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ epoch }),
    }
  );

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`API Error ${response.status}: ${errorBody}`);
  }
  // No need to parse response - endpoint returns void
}

/**
 * Get all pending GKDK distributions for an employee
 */
export async function getPendingGKDKForEmployee(
  employeeKeyId: string
): Promise<GKDKDistributionResponse[]> {
  return apiRequest<GKDKDistributionResponse[]>(
    `/authority-keys/gkdk-distributions/pending/${employeeKeyId}`
  );
}
